import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { RefreshCw, CheckCircle, XCircle, AlertCircle, Database, Play } from "lucide-react";
import { toast } from "sonner";

const configVacia = () => ({
  host: "", port: "3306", database: "", username: "", password: "",
  tabla_ventas: "ventas", col_cedula: "cedula", col_producto: "producto",
  col_cantidad: "cantidad", col_fecha: "fecha",
});

export default function ConfigERP() {
  const [config, setConfig] = useState(configVacia());
  const [guardada, setGuardada] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [syncResult, setSyncResult] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/erp/config`);
      if (data && data.host) {
        setConfig({
          host: data.host || "",
          port: String(data.port || 3306),
          database: data.database || "",
          username: data.username || "",
          password: "",  // never shown back
          tabla_ventas: data.tabla_ventas || "ventas",
          col_cedula: data.col_cedula || "cedula",
          col_producto: data.col_producto || "producto",
          col_cantidad: data.col_cantidad || "cantidad",
          col_fecha: data.col_fecha || "fecha",
        });
        setLastSync(data.ultimo_sync);
        setGuardada(true);
      }
    } catch { /* not configured yet */ }
  };

  useEffect(() => { load(); }, []);

  const set = (f) => (e) => setConfig(c => ({ ...c, [f]: e.target.value }));

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/erp/config`, {
        ...config,
        port: parseInt(config.port),
      });
      toast.success("Configuración guardada");
      setGuardada(true);
      setTestResult(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    }
  };

  const handleTest = async () => {
    setTestResult(null);
    try {
      const { data } = await axios.post(`${API}/erp/test-conexion`);
      setTestResult(data);
    } catch (err) {
      setTestResult({ ok: false, error: err.response?.data?.detail || "Error de conexión" });
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const { data } = await axios.post(`${API}/erp/sync`);
      setSyncResult(data);
      if (data.error) {
        toast.error(data.error);
      } else {
        toast.success(`Sincronización completa: ${data.notificaciones_creadas} notificaciones creadas`);
        load();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error durante la sincronización");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Configuración ERP</h1>
        <p className="text-sm text-slate-500">Conecta con tu sistema de punto de venta (MySQL) para detectar compras automáticamente</p>
      </div>

      {/* Connection form */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Database className="w-4 h-4 text-slate-400" />
            Conexión MySQL
          </CardTitle>
          <CardDescription>Datos de acceso al servidor de base de datos del ERP</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label>Host / IP del servidor *</Label>
                <Input value={config.host} onChange={set("host")} placeholder="ej: 192.168.1.10 o mi-erp.com" required />
              </div>
              <div>
                <Label>Puerto</Label>
                <Input type="number" value={config.port} onChange={set("port")} placeholder="3306" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Base de datos *</Label>
                <Input value={config.database} onChange={set("database")} placeholder="nombre de la BD" required />
              </div>
              <div>
                <Label>Usuario *</Label>
                <Input value={config.username} onChange={set("username")} required />
              </div>
            </div>
            <div>
              <Label>Contraseña *</Label>
              <Input type="password" value={config.password} onChange={set("password")} placeholder={guardada ? "••••••••  (dejar vacío para no cambiar)" : "contraseña"} required={!guardada} />
            </div>

            <hr className="border-slate-100" />

            <div>
              <p className="text-sm font-medium text-slate-700 mb-3">Mapeo de la tabla de ventas</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Nombre de la tabla *</Label>
                  <Input value={config.tabla_ventas} onChange={set("tabla_ventas")} placeholder="ventas" required />
                </div>
                <div>
                  <Label>Columna de cédula *</Label>
                  <Input value={config.col_cedula} onChange={set("col_cedula")} placeholder="cedula" required />
                </div>
                <div>
                  <Label>Columna de producto *</Label>
                  <Input value={config.col_producto} onChange={set("col_producto")} placeholder="producto" required />
                </div>
                <div>
                  <Label>Columna de cantidad (tabletas) *</Label>
                  <Input value={config.col_cantidad} onChange={set("col_cantidad")} placeholder="cantidad" required />
                </div>
                <div className="col-span-2">
                  <Label>Columna de fecha *</Label>
                  <Input value={config.col_fecha} onChange={set("col_fecha")} placeholder="fecha" required />
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-2">
                Estos son los nombres exactos de las columnas en tu tabla de ventas del ERP.
              </p>
            </div>

            <div className="flex gap-2">
              <Button type="submit" className="bg-red-700 hover:bg-red-800 text-white flex-1">
                Guardar configuración
              </Button>
              {guardada && (
                <Button type="button" variant="outline" onClick={handleTest}>
                  Probar conexión
                </Button>
              )}
            </div>
          </form>

          {/* Test result */}
          {testResult && (
            <div className={`mt-3 flex items-start gap-2 rounded-lg px-4 py-3 text-sm ${testResult.ok ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
              {testResult.ok
                ? <CheckCircle className="w-4 h-4 mt-0.5 shrink-0" />
                : <XCircle className="w-4 h-4 mt-0.5 shrink-0" />}
              <span>{testResult.ok ? testResult.mensaje : testResult.error}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sync panel */}
      {guardada && (
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <RefreshCw className="w-4 h-4 text-slate-400" />
              Sincronización manual
            </CardTitle>
            <CardDescription>
              Busca en el ERP las compras de clientes registrados y programa las notificaciones correspondientes
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {lastSync && (
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <AlertCircle className="w-3 h-3" />
                Último sync: {new Date(lastSync).toLocaleString("es-VE")}
              </div>
            )}

            <Button
              onClick={handleSync}
              disabled={syncing}
              className="bg-slate-800 hover:bg-slate-900 text-white"
            >
              {syncing
                ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Sincronizando…</>
                : <><Play className="w-4 h-4 mr-2" /> Sincronizar ahora</>}
            </Button>

            {syncResult && !syncResult.error && (
              <div className="bg-green-50 rounded-lg p-4 text-sm text-green-800 space-y-1">
                <p className="font-medium">Sincronización completada</p>
                <p>Ventas procesadas: <strong>{syncResult.ventas_procesadas}</strong></p>
                <p>Compras coincidentes con clientes: <strong>{syncResult.coincidencias}</strong></p>
                <p>Notificaciones programadas: <strong>{syncResult.notificaciones_creadas}</strong></p>
              </div>
            )}

            <div className="bg-slate-50 rounded-lg p-4 text-xs text-slate-500 space-y-1">
              <p className="font-medium text-slate-600">¿Cómo funciona la sincronización?</p>
              <ol className="list-decimal pl-4 space-y-1">
                <li>El sistema consulta la tabla de ventas del ERP buscando compras desde el último sync</li>
                <li>Para cada venta, busca en la cédula si hay un cliente inscrito en el programa</li>
                <li>Si el producto vendido coincide con el medicamento inscrito del cliente, registra la compra</li>
                <li>Calcula cuándo se van a acabar las pastillas (cantidad ÷ dosis diaria) y programa la notificación</li>
              </ol>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
